import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import BaseBeebeeComponent from './base-beebee-component.js';
import { dropTask, restartableTask, timeout } from 'ember-concurrency';
import { action, set } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { typeOf } from '@ember/utils';
import { debug } from '@ember/debug';
import { beebeeAjaxDelete, beebeeAjaxPost } from '../lib/utility/ajax.js';
import { clearAttribute } from '../lib/utility/strings.js';
import 'moment-timezone';
import 'ember-inflector';
import '../lib/utility/province.js';
import { notifyUserError } from '../lib/utility/misc.js';
import { inject } from '@ember/service';
import getConfig from '../lib/get-config.js';

var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5;
const DEBOUNCE_MS = 500;
const UI_ANIMATION_DELAY = 250;
const ASSOCIATION_NAMES = Object.freeze(['related_contacts', 'related_products', 'related_projects', 'products', 'projects', 'payments', 'events', 'invoices', 'expenses', 'workhours']);
const TOOLTIP_SELECTOR = '.dhtmlXTooltip.tooltip';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      Glimmer Octane base class for every `*-edit` component
 *              (templateless; subclasses supply their own template + wiring).
 *   extends:   BaseBeebeeComponent (same addon, src/components/base-beebee-component.js)
 *   template:  none (templateless — see SUBCLASS WIRING CONTRACT below)
 *   args:      @item, @applicationController, @transition, @afterSave,
 *              @updateModelAfterSave
 *   uses:      ember-concurrency (dropTask, restartableTask, timeout),
 *              ../lib/utility (notifyUserError, clearAttribute, beebeeAjaxPost,
 *              beebeeAjaxDelete), ../lib/get-config
 *   siblings:  base-beebee-component.js (parent — role/state getters +
 *              reload/editItem/baseSetup)
 *   tests:     base-edit-component-test.js (integration + unit),
 *              base-edit-component-wave4-test.js, base-edit-component-wave5-test.js
 *   gotchas:   cfCalculateTask captures `item` before two yields (custom_fields
 *              fetch + the calc POST) and re-checks `this.args.item !== item`
 *              before writing back calculated values — a swapped/torn-down
 *              @item mid-task would otherwise mutate a detached record set.
 *              updateItemTask must not navigate away when saveRecordRelated
 *              resolves `false` (a refused write, not a rejection) — leaving
 *              the edit screen would discard whatever the user had typed.
 *
 * Glimmer Octane base for every `*-edit` component (mega-cluster, §10 Layer 1).
 * NATIVE `class extends BaseBeebeeComponent` (no `.extend()`). Replaces the
 * Classic `component-edit` MIXIN (mixin-removal 1.5/3.1). Owns the
 * save/remove/calculate tasks + association/fiscal-contact helpers; inherits all
 * `BaseBeebeeComponent` role/state getters + `reload`/`editItem`/`baseSetup`.
 *
 * TEMPLATELESS: each `*-edit` subclass owns its template (was
 * `BaseEditComponent.extend({ layout })`). `@item` / `@applicationController` /
 * `@transition` / `@afterSave` / `@updateModelAfterSave` arrive as args.
 *
 * SUBCLASS WIRING CONTRACT (Layer 2/3 templates MUST add these to their root):
 *   - `{{did-insert this.baseSetup}}` — inherited scroll-top animate (from
 *     BaseBeebeeComponent).
 *   - `{{did-insert this.editSetup}}` — hides DHTMLX tooltips + resets item state
 *     (replaces this base's `didInsertElement`). NEVER `this.element`.
 *   - `{{will-destroy this.editTeardown}}` — restores DHTMLX tooltips (replaces
 *     `willDestroyElement`). Teardown MUST run or tooltips stay hidden.
 *   - actions unchanged by name: `{{this.toggleArchivied}}`, `{{this.toggleRemove}}`
 *     (was `{{action 'toggleRemove' true}}` → `{{fn this.toggleRemove true}}`),
 *     `{{this.setstatusList}}` (→ `{{fn this.setstatusList status}}`),
 *     `{{this.updateItem}}`, `{{this.removeItem}}`.
 *   - tasks: `this.updateItemTask` / `this.removeItemTask` / `this.cfCalculateTask`
 *     (templates `(perform this.updateItemTask)` etc.; `.isRunning` via
 *     `this.updateItemTask.isRunning`).
 *   - getter `allAssociation` unchanged by name.
 *   - `@tracked buttonDisabled` / `closeAfterSave` / `isNew` are local state
 *     (were plain Classic fields).
 */
let BaseEditComponent = (_class = class BaseEditComponent extends BaseBeebeeComponent {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "store", _descriptor, this);
    _initializerDefineProperty(this, "notify", _descriptor2, this);
    _initializerDefineProperty(this, "buttonDisabled", _descriptor3, this);
    _initializerDefineProperty(this, "closeAfterSave", _descriptor4, this);
    _initializerDefineProperty(this, "isNew", _descriptor5, this);
  }
  /**
   * Subclass templates wire this via `{{did-insert this.editSetup}}` (was the
   * mixin's `didInsertElement`). No DOM element needed — pure state reset + the
   * document-wide tooltip hide.
   */
  editSetup() {
    this._hideTooltips();
    this._resetItemState();
  }

  /**
   * Subclass templates wire this via `{{will-destroy this.editTeardown}}` (was
   * `willDestroyElement`).
   */
  editTeardown() {
    this._showTooltips();
  }
  _hideTooltips() {
    document.querySelectorAll(TOOLTIP_SELECTOR).forEach(el => {
      el.style.display = 'none';
    });
  }
  _showTooltips() {
    document.querySelectorAll(TOOLTIP_SELECTOR).forEach(el => {
      Object.assign(el.style, {
        visibility: 'hidden',
        display: 'block'
      });
    });
  }

  // Subclasses reassign the current record after an intermediate save: the
  // attachment-tab switch (and project-state change) persist a still-`isNew`
  // draft, then re-point to the store record the server returned — a DIFFERENT
  // instance, because `saveRecordRelated` persists via `pushPayload` and the
  // draft keeps `id: null`. They surface it through a `get item()` override
  // backed by `_itemOverride`. The save/remove/archive paths below MUST operate
  // on that record, not the stale `@item` arg — otherwise the Salva button
  // re-POSTs the untouched draft and the server creates a duplicate. This base
  // fallback returns the arg unchanged for components that never reassign.
  get item() {
    return this.args.item;
  }
  _resetItemState() {
    if (this.item) {
      set(this.item, 'confirmRemoveItem', null);
    }
    this.buttonDisabled = false;
  }
  _buildAssociationsArray() {
    return ASSOCIATION_NAMES.reduce((acc, associationName) => {
      const itemCount = this.item.get(`total_${associationName}_number`);
      if (itemCount) {
        acc.push({
          name: this.session.me?.get(`organization.${associationName}_name`),
          count: itemCount,
          items: this.item[associationName]
        });
      }
      return acc;
    }, []);
  }
  _invalidateIndexCache() {
    const modelName = this.args.item.constructor.modelName;
    if (this.args.applicationController.indexLoaded) {
      set(this.args.applicationController, `indexLoaded.${modelName}`, {});
    }
  }
  _closeCardIfOpen(id) {
    const openCard = this.args.applicationController.get('openCards')?.find(i => i.item.id === id);
    if (openCard) {
      this.args.applicationController.closeCardTask.perform(openCard);
    }
  }
  _prepareCustomFieldsData(customFields) {
    const data = [];
    let hasCalculated = false;
    customFields.forEach(item => {
      const orgCf = item.organization_custom_field;
      if (orgCf.data_type === 'calculated') {
        hasCalculated = true;
      }
      const attributes = {};
      item.eachAttribute(name => {
        attributes[name] = clearAttribute(item.get(name));
      });
      data.push({
        id: item.id,
        organization_custom_field_id: orgCf.id,
        data_type: orgCf.data_type,
        model_related: orgCf.model_related,
        name: orgCf.name,
        attributes
      });
    });
    return {
      data,
      hasCalculated
    };
  }
  _applyCalculatedValues(calculatedFields, customFields) {
    if (!Array.isArray(calculatedFields)) return;
    calculatedFields.forEach(calculated => {
      const cf = customFields.find(i => i.get('organization_custom_field.id') === `${calculated.organization_custom_field_id}`);
      if (cf) {
        if (calculated.value != null) {
          set(cf, 'value', calculated.value);
        }
        if (calculated.number_value != null) {
          set(cf, 'number_value', calculated.number_value);
        }
        if (calculated.boolean_value != null) {
          set(cf, 'boolean_value', calculated.boolean_value);
        }
      }
    });
  }
  get allAssociation() {
    return this._buildAssociationsArray();
  }
  checkUser() {
    const item = this.item || this.args.item;

    // Both `this.item` and the arg are gone once the host starts tearing down
    // (the args proxy hands back `undefined` while the component is still
    // alive), and an archive/remove click that lands in that gap reached
    // `item.constructor` on nothing (Sentry EMBER-MEDICAL-2XH, via
    // toggleRemove). There is no record to archive or delete then, so refuse
    // the action instead of throwing — every caller already treats `false` as
    // "do not proceed", and `toggleRemove`/`toggleArchivied` would otherwise
    // go on to `set()` on the same missing record.
    if (!item) {
      return false;
    }

    // `constructor.modelName` only exists on an Ember Data record; a few hosts
    // pass a plain object, which has always fallen through to the `!== 'contact'`
    // branch below. Keep that behaviour, just without the throw.
    const modelName = item.constructor?.modelName;
    if (modelName !== 'contact' || item.archivied) {
      return true;
    }
    if (item.username) {
      this.notify.warning(this.intl.t('contact with username'));
      return false;
    }
    if (item.organization_admin) {
      this.notify.warning(this.intl.t('contact admin'));
      return false;
    }
    return true;
  }
  updateExpenseUser(item, storeItem) {
    item.setProperties({
      fisica_giuridica: storeItem.fisica_giuridica ?? false,
      society_name: storeItem.ragione_sociale,
      society_first_name: storeItem.first_name,
      society_last_name: storeItem.last_name,
      society_piva: storeItem.piva,
      society_cf: storeItem.cod_fiscale
    });
  }
  updateInvoiceContact(item, storeItem) {
    item.setProperties({
      contact_fisica_giuridica: storeItem.fisica_giuridica ?? false,
      contact_ente_pa: storeItem.ente_pa,
      contact_name: storeItem.ragione_sociale,
      contact_first_name: storeItem.first_name,
      contact_last_name: storeItem.last_name,
      contact_piva: storeItem.piva,
      contact_cf: storeItem.cod_fiscale,
      pec_destinatario: storeItem.pec,
      codice_destinatario: storeItem.codice_destinatario
    });
    const sourceAddress = storeItem.addresses?.find(a => a.is_invoice === true) || storeItem.addresses?.[0];
    let newAddress;
    if (sourceAddress) {
      const attributeNames = Array.from(sourceAddress.constructor.attributes.keys());
      const attributes = sourceAddress.getProperties(...attributeNames);
      newAddress = this.store.createRecord('address', attributes);
    } else {
      newAddress = this.store.createRecord('address', {});
    }
    set(item, 'contact_address', newAddress);
  }
  *removeItemTask() {
    const item = this.item || this.args.item;
    const itemId = item.id;
    const toDelete = item.confirmRemoveItem?.toDelete;
    if (!item.confirmRemoveItem) {
      return;
    }
    if (Array.isArray(item.voucher_json)) {
      this.notify.warning(this.intl.t('you cannot delete a payment'));
      return;
    }
    yield timeout(UI_ANIMATION_DELAY);
    try {
      let delItem = null;
      if (toDelete) {
        if (item.recursive_rule) {
          delItem = yield beebeeAjaxDelete(`${getConfig(this).APP.urlApi}/events/${itemId}?recursive_rule=${item.recursive_rule}`, this.session.token);
        } else {
          delItem = yield this.args.applicationController.destroyRecord(item);
        }
      } else {
        set(item, 'archivied', !item.archivied);
        // Persist the SAME record we just archived: `this.item` may have been
        // re-pointed during the animation delay above, so let updateItemTask
        // save `item` rather than re-reading a possibly-different active record.
        delItem = yield this.updateItemTask.perform(true, item);
      }
      if (this.isDestroying || this.isDestroyed) return;
      set(this.args.applicationController, 'confirmRemoveItem', null);
      if (delItem) {
        this._invalidateIndexCache();
        set(this.args.applicationController, 'reloadIndex', !this.args.applicationController.reloadIndex);
        set(this.args.applicationController, 'modalComponents', []);
        if (toDelete) {
          this._closeCardIfOpen(itemId);
        }
      }
    } catch (e) {
      notifyUserError(this.notify, e, this.intl);
    }
  }
  *cfCalculateTask() {
    yield timeout(DEBOUNCE_MS);

    // The current record — the `_itemOverride` a subclass may have re-pointed to
    // the persisted store record after an intermediate save, else the `@item`
    // arg. The debounce above can null it (pane reload / teardown). Guard first.
    const item = this.item;
    if (!item) return;
    const customFields = yield item.custom_fields;
    const {
      data,
      hasCalculated
    } = this._prepareCustomFieldsData(customFields);
    if (!hasCalculated) {
      return;
    }
    try {
      const response = yield beebeeAjaxPost(`${getConfig(this).APP.urlApi}/calc_custom_fields`, this.session.token, {
        custom_fields: data
      });

      // Two awaits (custom_fields + the POST) passed since we captured `item`.
      // If the host swapped/tore down `@item` meanwhile, `customFields` now points
      // at a detached record set — `set(cf, 'value', …)` inside would throw on a
      // destroyed CF. Bail unless we are still on the same, live record.
      if (this.isDestroying || this.isDestroyed || this.item !== item) {
        return;
      }
      this._applyCalculatedValues(response?.data?.calculated, customFields);
    } catch (e) {
      debug('cfCalculateTask error:', e);
    }
  }
  *updateItemTask(transitionToView = true, item = this.item || this.args.item) {
    const isExisting = Boolean(item.id);
    const modelName = item.constructor.modelName;
    const savedItem = yield this.args.applicationController.saveRecordRelated(item);
    if (this.isDestroying || this.isDestroyed) return null;
    if (!savedItem) {
      // saveRecordRelated never rejects: it resolves `false` after notifying the
      // user. On a 422 it also fills `item.errors` so the flagged fields can be
      // corrected; on a 500 / network failure it rolls the record back and leaves
      // `errors` empty. Both are refused writes, so neither may navigate away —
      // leaving the edit screen here discarded whatever the user had typed.
      if (!(this.isDestroying || this.isDestroyed)) {
        this.buttonDisabled = false;
      }
      return null;
    }
    const savedId = savedItem[0]?.data?.id;
    const storeItem = savedId ? this.store.peekRecord(modelName, savedId) : null;
    if (storeItem) {
      if (typeOf(this.args.transition) === 'function') {
        this.args.transition(storeItem);
      }
      if (this.args.updateModelAfterSave) {
        const {
          action: afterAction,
          item: targetItem
        } = this.args.updateModelAfterSave;
        if (afterAction === 'updateInvoiceContact') {
          this.updateInvoiceContact(targetItem, storeItem);
        } else if (afterAction === 'updateExpenseUser') {
          this.updateExpenseUser(targetItem, storeItem);
        }
      }
      if (typeOf(this.args.afterSave) === 'function') {
        this.args.afterSave(storeItem);
      }
      this._invalidateIndexCache();
      if (!(this.isDestroying || this.isDestroyed)) {
        this.buttonDisabled = false;
      }
      if (transitionToView && !isExisting && !this.args.applicationController.itemPreview) {
        this.args.applicationController.myTransitionToView(`${modelName}s`, storeItem.id);
      }
    } else {
      if (transitionToView && !isExisting) {
        this.args.applicationController.myTransitionToView('index');
      }
    }
    if (this.closeAfterSave) {
      const modalComponents = [...(this.args.applicationController.modalComponents || [])];
      const idx = modalComponents.indexOf(this.args.applicationController.modalComponent);
      if (idx !== -1) {
        modalComponents.splice(idx, 1);
      }
      set(this.args.applicationController, 'modalComponents', modalComponents);
    }
    return savedItem[0] || null;
  }
  toggleArchivied() {
    if (this.checkUser()) {
      if (this.updateItemTask.isRunning) {
        return;
      }
      set(this.item, 'archivied', !this.item.archivied);
      this.updateItemTask.perform();
    }
  }
  toggleRemove(toDelete = false) {
    if (!this.checkUser()) {
      return;
    }
    const allAssociation = this._buildAssociationsArray();
    set(this.item, 'confirmRemoveItem', {
      toDelete
    });
    set(this.args.applicationController, 'confirmRemoveItem', {
      item: this.item,
      allAssociation,
      removeItemTask: this.removeItemTask,
      toDelete
    });
  }
  setstatusList(status) {
    set(this.item, 'status', status);
  }
  updateItem() {
    this.updateItemTask.perform();
  }
  removeItem() {
    this.removeItemTask.perform();
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "store", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "notify", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "buttonDisabled", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "closeAfterSave", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return true;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "isNew", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "editSetup", [action], Object.getOwnPropertyDescriptor(_class.prototype, "editSetup"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "editTeardown", [action], Object.getOwnPropertyDescriptor(_class.prototype, "editTeardown"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "removeItemTask", [dropTask], Object.getOwnPropertyDescriptor(_class.prototype, "removeItemTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cfCalculateTask", [restartableTask], Object.getOwnPropertyDescriptor(_class.prototype, "cfCalculateTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "updateItemTask", [dropTask], Object.getOwnPropertyDescriptor(_class.prototype, "updateItemTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "toggleArchivied", [action], Object.getOwnPropertyDescriptor(_class.prototype, "toggleArchivied"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "toggleRemove", [action], Object.getOwnPropertyDescriptor(_class.prototype, "toggleRemove"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setstatusList", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setstatusList"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "updateItem", [action], Object.getOwnPropertyDescriptor(_class.prototype, "updateItem"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "removeItem", [action], Object.getOwnPropertyDescriptor(_class.prototype, "removeItem"), _class.prototype), _class);

export { BaseEditComponent as default };
//# sourceMappingURL=base-edit-component.js.map
